import React, { FC } from 'react';

import { Box, Center, Flex, HStack, Icon, Stack, Text } from '@chakra-ui/react';
import { GoTriangleRight } from 'react-icons/go';
import { IoInformation } from 'react-icons/io5';
import { customTheme } from 'theme';

import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { GettingStartedComponent } from 'components/DataDisplay/Utils/GettingStarted/GettingStartedComponent';
import { getIcon, IconTypes, PrimaryButton } from 'components/index';

interface Props {
  onGetStarted: () => void;
}

const steps = [
  {
    header: 'Data classification',
    description:
      'Before creating data policies, Ariksa should classify your sensitive data in the data sources',
    iconType: IconTypes.SensitiveData,
  },
  {
    header: 'Zero Trust Data Access',
    description:
      'You can fully mask all data access as a baseline for zero-trust. Your new policies will override this masking based on roles',
    iconType: IconTypes.Hide,
  },
  {
    header: 'Considerations for Zero Trust',
    description:
      'If you enable zero trust, all sensitive data will be fully masked. You can skip this entirely and create new data masking policies',
    iconType: IconTypes.ExclamationTriangle,
  },
  {
    header: 'Data Masking Policies',
    description:
      'Create new policies to enforce granular data masking for every data type for every role used by your teams',
    iconType: IconTypes.PolicyScroll,
  },
];

export const DataAccessGettingStarted: FC<Props> = props => {
  const { onGetStarted } = props;

  const gettingStartedBox = item => (
    <BorderedBox
      bg={customTheme.colors.gettingStartedItemBg}
      py={6}
      px={8}
      w={72}
      h={72}
    >
      <Stack spacing={6}>
        <HStack spacing={6} align="flex-start">
          <Box boxSize={8} mt={1}>
            <Center>{getIcon(item.iconType)}</Center>
          </Box>
          <Box fontSize="22px" flex={1} fontWeight={600}>
            {item.header}
          </Box>
        </HStack>
        <Box fontSize="17px" pl={2}>
          {item.description}
        </Box>
      </Stack>
    </BorderedBox>
  );

  const content = (
    <Stack spacing={{ base: 8, lg: 16 }} w="full">
      <HStack spacing={2} w="full">
        <Center
          boxSize={10}
          border="1px solid"
          borderColor="whiteAlpha.700"
          borderRadius={4}
          flexShrink={0}
          p={1}
        >
          <Icon as={IoInformation} w="full" h="full" />
        </Center>
        <Text fontSize="xl" fontWeight={600}>
          Data Classification, Zero Trust Data Access &amp; Data Masking
          Policies
        </Text>
      </HStack>

      <HStack w="full" justify="space-between">
        {steps.map((o, index) => (
          <>
            {gettingStartedBox(o)}
            {index !== steps?.length - 1 && (
              <Center
                boxSize={14}
                flexShrink={0}
                color={customTheme.colors.gettingStartedItemBg}
              >
                <Icon as={GoTriangleRight} w="full" h="full" />
              </Center>
            )}
          </>
        ))}
      </HStack>

      <Flex justify="flex-end">
        <Box>
          <PrimaryButton
            size="md"
            onClick={onGetStarted}
            bg="primary"
            color="white"
          >
            GET STARTED
          </PrimaryButton>
        </Box>
      </Flex>
    </Stack>
  );

  return <GettingStartedComponent content={content} />;
};
